import { 
    MessageSquare, 
    ShieldCheck, 
    Database, 
    Briefcase, 
    Calculator, 
    Building2, 
    ShieldAlert, 
    Lightbulb, 
    Users, 
    Zap, 
    Stethoscope, 
    Truck, 
    HardHat, 
    PiggyBank, 
    Smartphone, 
    GraduationCap, 
    ShoppingBag, 
    Layers, 
    Bell, 
    Gavel, 
    ClipboardCheck, 
    Languages, 
    BarChart3,
    Search
} from "lucide-react";

export type AgentCategoryId = "Solicitud" | "Transversal" | "Sectorial" | "Productividad";

export interface AgentMeta {
    id: string;
    name: string;
    icon: any;
    color: string;
    bgHover: string;
    description: string;
    welcomeMessage: string;
    suggestedQueries: string[];
    category: AgentCategoryId;
}

export const SPECIALIZED_AGENTS_META: Record<string, AgentMeta> = {
    // --- SOLICITUD ---
    "peticion_agent": {
        id: "peticion_agent",
        name: "Respuestas a Peticiones (Ley 1755)",
        icon: MessageSquare,
        color: "text-blue-600",
        bgHover: "hover:bg-blue-50",
        category: "Solicitud",
        description: "Gestión experta de Derechos de Petición.",
        welcomeMessage: "Hola. Soy tu experto en Derechos de Petición. Puedo ayudarte a redactar respuestas, calcular términos legales según la Ley 1755 y detectar riesgos normativos.",
        suggestedQueries: [
            "¿Cuál es el plazo para responder una petición de información?",
            "Redactar respuesta a petición de documentos reservados",
            "¿Cómo invocar el secreto empresarial en una respuesta?"
        ]
    },
    "pqrsd_agent": {
        id: "pqrsd_agent",
        name: "PQRSD Empresas (Gestión de Quejas)",
        icon: ShieldCheck,
        color: "text-teal-600",
        bgHover: "hover:bg-teal-50",
        category: "Solicitud",
        description: "Procesamiento integral de quejas y reclamos.",
        welcomeMessage: "Especialista en PQRSD a tu servicio. Analizo quejas, sugiero compensaciones y convierto reclamos en oportunidades de mejora.",
        suggestedQueries: [
            "Sugerir respuesta para queja por falla en el servicio",
            "¿Qué compensación legal aplica para este reclamo?",
            "Clasificar esta lista de PQRSD por nivel de riesgo"
        ]
    },
    "habeas_data_agent": {
        id: "habeas_data_agent",
        name: "Hábeas Data (Ley 1581)",
        icon: Database,
        color: "text-indigo-600",
        bgHover: "hover:bg-indigo-50",
        category: "Solicitud",
        description: "Protección de datos y cumplimiento SIC.",
        welcomeMessage: "Experto en Protección de Datos Personales (Ley 1581). Te ayudo con solicitudes de acceso, rectificación y cumplimiento ante la SIC.",
        suggestedQueries: [
            "Responder solicitud de eliminación de datos personales",
            "¿Cómo cumplir con el principio de finalidad?",
            "Redactar política de tratamiento de datos personales"
        ]
    },

    // --- TRANSVERSAL ---
    "laboral_agent": {
        id: "laboral_agent",
        name: "Laboral y RRHH",
        icon: Briefcase,
        color: "text-orange-600",
        bgHover: "hover:bg-orange-50",
        category: "Transversal",
        description: "Contratos, liquidaciones y estabilidad reforzada.",
        welcomeMessage: "Consultor experto en Derecho Laboral Colombiano. Analizo contratos, despidos y estabilidad laboral.",
        suggestedQueries: [
            "¿Cómo manejar un despido con estabilidad reforzada?",
            "Calcular liquidación para contrato a término fijo",
            "Responder requerimiento por presunto acoso laboral"
        ]
    },
    "tributario_agent": {
        id: "tributario_agent",
        name: "Tributario y DIAN",
        icon: Calculator,
        color: "text-red-600",
        bgHover: "hover:bg-red-50",
        category: "Transversal",
        description: "Requerimientos DIAN, IVA y conceptos de renta.",
        welcomeMessage: "Especialista Tributario. Te asisto en la defensa ante la DIAN y el análisis de impuestos nacionales.",
        suggestedQueries: [
            "Concepto sobre deducibilidad de gastos en renta",
            "Responder requerimiento especial de la DIAN",
            "¿Qué sanciones aplican por error en medios magnéticos?"
        ]
    },
    "comercial_agent": {
        id: "comercial_agent",
        name: "Comercial y Societario",
        icon: Building2,
        color: "text-emerald-600",
        bgHover: "hover:bg-emerald-50",
        category: "Transversal",
        description: "Constitución de sociedades y contratos mercantiles.",
        welcomeMessage: "Asesor Comercial y Societario. Experto en estructuración de empresas y contratos de negocios.",
        suggestedQueries: [
            "Redactar acta de asamblea para reforma de estatutos",
            "¿Cómo estructurar un contrato de agencia comercial?",
            "Requisitos para disolución y liquidación de una SAS"
        ]
    },
    "compliance_agent": {
        id: "compliance_agent",
        name: "Cumplimiento (SAGRILAFT)",
        icon: ShieldAlert,
        color: "text-slate-700",
        bgHover: "hover:bg-slate-50",
        category: "Transversal",
        description: "Prevención de riesgos penales y SAGRILAFT.",
        welcomeMessage: "Oficial de Cumplimiento IA. Monitoreo SAGRILAFT y programas de transparencia empresarial.",
        suggestedQueries: [
            "¿Cómo identificar una operación sospechosa?",
            "Revisar política anticorrupción según Ley 2195",
            "Actualizar manual de cumplimiento SAGRILAFT"
        ]
    },
    "propiedad_intelectual_agent": {
        id: "propiedad_intelectual_agent",
        name: "Propiedad Intelectual",
        icon: Lightbulb,
        color: "text-yellow-600",
        bgHover: "hover:bg-yellow-50",
        category: "Transversal",
        description: "Marcas, patentes y derechos de autor.",
        welcomeMessage: "Experto en PI. Te ayudo a proteger tus creaciones, marcas e invenciones ante la SIC.",
        suggestedQueries: [
            "Responder oposición de marca ante la SIC",
            "¿Cuáles son los requisitos de patentabilidad?",
            "Redactar contrato de cesión de derechos de autor"
        ]
    },
    "consumo_agent": {
        id: "consumo_agent",
        name: "Protección al Consumidor",
        icon: Users,
        color: "text-pink-600",
        bgHover: "hover:bg-pink-50",
        category: "Transversal",
        description: "Garantías, retracto y defensa ante la SIC.",
        welcomeMessage: "Especialista en Derecho del Consumo. Defiendo tus intereses en garantías y publicidad engañosa.",
        suggestedQueries: [
            "¿Cómo aplicar el derecho al retracto en e-commerce?",
            "Responder demanda de protección al consumidor",
            "Analizar cumplimiento de garantía legal"
        ]
    },

    // --- SECTORIAL ---
    "servicios_publicos_agent": {
        id: "servicios_publicos_agent",
        name: "Servicios Públicos",
        icon: Zap,
        color: "text-amber-600",
        bgHover: "hover:bg-amber-50",
        category: "Sectorial",
        description: "Regulación de energía, agua y telecomunicaciones.",
        welcomeMessage: "Experto en Servicios Públicos Domiciliarios. Aplico la Ley 142 y regulaciones de la CREG/CRA.",
        suggestedQueries: [
            "Recurso de reposición por cobro de servicios no prestados",
            "¿Cómo defenderse ante la Superservicios?",
            "Trámite de conexión para nuevos desarrollos"
        ]
    },
    "salud_agent": {
        id: "salud_agent",
        name: "Sector Salud y EPS",
        icon: Stethoscope,
        color: "text-rose-600",
        bgHover: "hover:bg-rose-50",
        category: "Sectorial",
        description: "Defensas de tutelas y trámites ante Invima.",
        welcomeMessage: "Consultor especializado en Salud. Gestión de tutelas y cumplimiento normativo sanitario.",
        suggestedQueries: [
            "Contestar tutela por negación de medicamentos No-POS",
            "Requisitos para registro sanitario INVIMA",
            "Responder PQR ante la Superintendencia de Salud"
        ]
    },
    "manufactura_agro_agent": {
        id: "manufactura_agro_agent",
        name: "Industria y Agro",
        icon: Truck,
        color: "text-green-700",
        bgHover: "hover:bg-green-50",
        category: "Sectorial",
        description: "Licencias ambientales y registros ICA.",
        welcomeMessage: "Especialista en Manufactura y Agro. Normativa ambiental, trazabilidad e inocuidad.",
        suggestedQueries: [
            "Requisitos para licencia ambiental de vertimientos",
            "Cumplimiento de normas ICA para exportación",
            "¿Cómo gestionar residuos peligrosos legalmente?"
        ]
    },
    "construccion_agent": {
        id: "construccion_agent",
        name: "Construcción",
        icon: HardHat,
        color: "text-stone-600",
        bgHover: "hover:bg-stone-50",
        category: "Sectorial",
        description: "Urbanismo, licencias y vicios ocultos.",
        welcomeMessage: "Experto en Derecho Inmobiliario y Construcción. Asesoría en licencias y estabilidad de obra.",
        suggestedQueries: [
            "Responder demanda por vicios ocultos en construcción",
            "Requisitos para licencia de urbanismo",
            "¿Cómo liquidar un contrato de obra civil?"
        ]
    },
    "finanzas_banca_agent": {
        id: "finanzas_banca_agent",
        name: "Finanzas y Banca",
        icon: PiggyBank,
        color: "text-gold-600",
        bgHover: "hover:bg-gold-50",
        category: "Sectorial",
        description: "Estatuto del Consumidor Financiero.",
        welcomeMessage: "Asesor Financiero y Bancario. Protección al consumidor financiero y trámites ante la SFC.",
        suggestedQueries: [
            "Responder queja ante el Defensor del Consumidor Financiero",
            "¿Cómo cumplir con la Circular Básica Jurídica?",
            "Analizar cláusulas abusivas en contratos de seguro"
        ]
    },
    "tech_startups_agent": {
        id: "tech_startups_agent",
        name: "Tech y Startups",
        icon: Smartphone,
        color: "text-cyan-600",
        bgHover: "hover:bg-cyan-50",
        category: "Sectorial",
        description: "Contratos SaaS, Equity y Vesting.",
        welcomeMessage: "Asesor Legal para Startups. Estructuro tu crecimiento desde el equity hasta el SaaS.",
        suggestedQueries: [
            "Redactar contrato SaaS con SLA",
            "¿Cómo estructurar un acuerdo de Vesting?",
            "Términos y condiciones para App Fintech"
        ]
    },
    "educacion_agent": {
        id: "educacion_agent",
        name: "Educación",
        icon: GraduationCap,
        color: "text-sky-700",
        bgHover: "hover:bg-sky-50",
        category: "Sectorial",
        description: "Reglamentos estudiantiles y menores.",
        welcomeMessage: "Experto en Derecho Educativo. Gestión de convivencia y protección de datos académicos.",
        suggestedQueries: [
            "Actualizar reglamento estudiantil según Ley 1620",
            "Manejo de datos sensibles de menores en colegios",
            "Manejo legal de deserción y cobros académicos"
        ]
    },
    "retail_comercio_agent": {
        id: "retail_comercio_agent",
        name: "Comercio y Retail",
        icon: ShoppingBag,
        color: "text-purple-600",
        bgHover: "hover:bg-purple-50",
        category: "Sectorial",
        description: "Proveedores y distribución masiva.",
        welcomeMessage: "Consultor para Retail. Optimizo tus relaciones con proveedores y cumplimiento en puntos de venta.",
        suggestedQueries: [
            "Cláusulas de exclusividad en contratos de suministro",
            "Políticas de cambios y devoluciones para grandes superficies",
            "Negociación de acuerdos de nivel de servicio con proveedores"
        ]
    },

    // --- PRODUCTIVIDAD ---
    "clasificador_agent": {
        id: "clasificador_agent",
        name: "Agente Clasificador",
        icon: Layers,
        color: "text-violet-600",
        bgHover: "hover:bg-violet-50",
        category: "Productividad",
        description: "Radicado automático y detección de riesgos.",
        welcomeMessage: "Soy tu Agente Clasificador. Sube un documento y determinaré su tipo, plazos y nivel de urgencia.",
        suggestedQueries: [
            "Clasificar el documento adjunto",
            "¿Cuál es el plazo perentorio para esta notificación?",
            "Identificar riesgos críticos en esta demanda"
        ]
    },
    "alertas_regulatorias_agent": {
        id: "alertas_regulatorias_agent",
        name: "Alertas Regulatorias",
        icon: Bell,
        color: "text-orange-500",
        bgHover: "hover:bg-orange-50",
        category: "Productividad",
        description: "Monitoreo de leyes y decretos por sector.",
        welcomeMessage: "Vigilante Normativo. Te informo sobre los cambios legales que impactan tu negocio.",
        suggestedQueries: [
            "Resumen de nuevas leyes para el sector salud",
            "Impacto del último decreto de la DIAN",
            "Alertas de cambios en contratación pública"
        ]
    },
    "preparacion_tutelas_agent": {
        id: "preparacion_tutelas_agent",
        name: "Preparación de Tutelas",
        icon: Gavel,
        color: "text-zinc-700",
        bgHover: "hover:bg-zinc-50",
        category: "Productividad",
        description: "Estructuración de tutelas y recursos.",
        welcomeMessage: "Arquitecto de Tutelas. Estructuro tus defensas y recursos con rigor técnico.",
        suggestedQueries: [
            "Redactar acción de tutela por debido proceso",
            "Estructurar recurso de apelación contra sentencia",
            "Identificar nulidades procesales en este caso"
        ]
    },
    "auditoria_interna_agent": {
        id: "auditoria_interna_agent",
        name: "Auditoría Legal",
        icon: ClipboardCheck,
        color: "text-blue-800",
        bgHover: "hover:bg-blue-50",
        category: "Productividad",
        description: "Revisión de gaps de cumplimiento interno.",
        welcomeMessage: "Auditor Legal Interno. Detecto brechas entre tus políticas y la normativa vigente.",
        suggestedQueries: [
            "Revisar manual de funciones frente a ley laboral",
            "Detectar brechas de cumplimiento en política de datos",
            "Auditar actas de asamblea de los últimos 2 años"
        ]
    },
    "multilingue_agent": {
        id: "multilingue_agent",
        name: "Adaptación Multilingüe",
        icon: Languages,
        color: "text-sky-600",
        bgHover: "hover:bg-sky-50",
        category: "Productividad",
        description: "Traducción y adaptación legal EN/ES.",
        welcomeMessage: "Traductor Legal Experto. Adapto conceptos entre Common Law y el Derecho Colombiano.",
        suggestedQueries: [
            "Adaptar contrato de servicios de inglés a ley colombiana",
            "Traducir términos de arbitraje internacional",
            "Glosario de términos para due diligence bilingüe"
        ]
    },
    "estadisticas_reportes_agent": {
        id: "estadisticas_reportes_agent",
        name: "Estadísticas y Reportes",
        icon: BarChart3,
        color: "text-indigo-700",
        bgHover: "hover:bg-indigo-50",
        category: "Productividad",
        description: "Dashboards y KPIs de gestión jurídica.",
        welcomeMessage: "Analista de Gestión Jurídica. Transformo tu volumen de casos en indicadores estratégicos.",
        suggestedQueries: [
            "Generar reporte de causas raíz de las PQRSD",
            "Visualizar tiempos promedio de respuesta",
            "KPIs de eficiencia del departamento legal"
        ]
    }
};

export const AGENT_CATEGORIES = [
    { id: "Solicitud", label: "Gestión de Solicitudes", icon: MessageSquare, description: "Peticiones, PQRSD y Protección de Datos." },
    { id: "Transversal", label: "Áreas del Derecho", icon: Layers, description: "Especialistas en ramas legales fundamentales." },
    { id: "Sectorial", label: "Sectores Económicos", icon: Building2, description: "Expertos en la normativa específica de tu industria." },
    { id: "Productividad", label: "Operaciones Legales", icon: Zap, description: "Herramientas para potenciar tu equipo jurídico." }
];
